'use client';

import { SvgChevrondown } from '@common/icon';
import { throttle } from 'es-toolkit';
import { motion, useAnimation } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';

interface MoreFloatingButtonProps {
  contentAreaSelector: string;
  recommendHometownNewsSelector: string;
  keyword: string;
}

export const MoreFloatingButton = ({
  contentAreaSelector,
  recommendHometownNewsSelector,
  keyword,
}: MoreFloatingButtonProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [contentsElement, setContentsElement] = useState<HTMLElement>();
  const [recommendHometownNewsElement, setRecommendHometownNewsElement] =
    useState<HTMLElement>();
  const [isShow, setIsShow] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const updateIsShow = useCallback(() => {
    if (
      contentsElement === undefined ||
      recommendHometownNewsElement === undefined
    )
      return;

    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;

    const contentsRect = contentsElement.getBoundingClientRect();
    const contentsTopFromDoc = contentsRect.top + scrollY;

    const recommendRect = recommendHometownNewsElement.getBoundingClientRect();
    const recommendTopFromDoc = recommendRect.top + scrollY;

    const isContentsScrollOveredHalf =
      contentsTopFromDoc + contentsElement.offsetHeight / 2 <
      scrollY + viewportHeight / 2;

    const isRecommendHometownNewsListScrollOvered =
      recommendTopFromDoc < scrollY + viewportHeight;

    setIsShow(
      isContentsScrollOveredHalf && !isRecommendHometownNewsListScrollOvered,
    );
  }, [contentsElement, recommendHometownNewsElement]);

  useEffect(() => {
    if (!isMounted) return;

    const contentsElement = document.getElementById(contentAreaSelector);
    if (contentsElement) {
      setContentsElement(contentsElement);
    }

    const recommendHometownNewsElement = document.getElementById(
      recommendHometownNewsSelector,
    );
    if (recommendHometownNewsElement) {
      setRecommendHometownNewsElement(recommendHometownNewsElement);
    }
  }, [isMounted, contentAreaSelector, recommendHometownNewsSelector]);

  useEffect(() => {
    if (contentsElement && recommendHometownNewsElement) {
      updateIsShow();
    }
  }, [contentsElement, recommendHometownNewsElement, updateIsShow]);

  useEffect(() => {
    const handleScroll = throttle(() => updateIsShow(), 200);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [updateIsShow]);

  useEffect(() => {
    controls.start({
      opacity: isShow ? SHOW_BUTTON_OPACITY : INITIALIZE_BUTTON_OPACITY,
      bottom: isShow ? SHOW_BUTTON_BOTTOM : INITIALIZE_BUTTON_BOTTOM,
      transition: { duration: 0.2 },
    });
  }, [isShow, controls]);

  const handleClick = () => {
    if (recommendHometownNewsElement === undefined) return;

    recommendHometownNewsElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <motion.button
      animate={controls}
      className="fixed inset-x-0 mx-auto size-fit p-0"
      disabled={!isShow}
      initial={{
        opacity: INITIALIZE_BUTTON_OPACITY,
        bottom: INITIALIZE_BUTTON_BOTTOM,
      }}
      type="button"
      onClick={handleClick}
    >
      <section className="flex h-40 w-full items-center justify-center gap-4 rounded-pill bg-Gray-black px-20">
        <p className="font-700 text-Gray-white typo-body4">
          {keyword} 소식 더보기
        </p>
        <SvgChevrondown className="size-14 text-Gray-white" />
      </section>
    </motion.button>
  );
};

const INITIALIZE_BUTTON_OPACITY = 0;
const INITIALIZE_BUTTON_BOTTOM = -40;
const SHOW_BUTTON_OPACITY = 1;
const SHOW_BUTTON_BOTTOM = 10;
